import React, { useEffect, useState } from 'react';
import API from '../api/axios';
import Header from '../components/Header';
import { Plus, Edit2, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';

const EMPTY = { name: '', icon: '🔧', image: '', description: '', order: 0 };

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get('/services/categories/admin/').then(r => setCategories(r.data)).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openModal = (cat = null) => {
    setForm(cat ? { ...cat } : EMPTY);
    setModal(cat ? 'edit' : 'add');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modal === 'add') {
        const { data } = await API.post('/services/categories/admin/', form);
        setCategories(c => [...c, data]);
        toast.success('Category added!');
      } else {
        const { data } = await API.put(`/services/categories/admin/${form.id}/`, form);
        setCategories(c => c.map(x => x.id === form.id ? data : x));
        toast.success('Category updated!');
      }
      setModal(null);
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await API.delete(`/services/categories/admin/${id}/`);
      setCategories(c => c.filter(x => x.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  const ICONS = ['🧹', '🔧', '⚡', '🎨', '🔌', '🐛', '🪚', '💆', '🏠', '🚿', '🛁', '🪴', '🚗', '🐾', '📦'];

  return (
    <div className="flex-1 overflow-auto">
      <Header title="Categories" subtitle={`${categories.length} categories`} />
      <div className="p-6 space-y-4">
        <div className="flex justify-end">
          <button onClick={() => openModal()} className="btn-primary flex items-center gap-2">
            <Plus size={16} /> Add Category
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {loading ? [...Array(6)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-32 bg-gray-200 rounded-xl mb-4" />
              <div className="h-4 bg-gray-200 rounded mb-2 w-3/4" />
              <div className="h-3 bg-gray-100 rounded" />
            </div>
          )) : categories.map(c => (
            <div key={c.id} className="card p-0 overflow-hidden hover:shadow-md transition-all">
              <div className="relative h-32 overflow-hidden">
                <img src={c.image || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=200&fit=crop'}
                  alt={c.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-3 left-3 text-3xl">{c.icon}</div>
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-bold text-gray-900">{c.name}</h4>
                  <span className="text-xs text-orange-500 font-semibold">{c.service_count} services</span>
                </div>
                <p className="text-xs text-gray-500 mb-3 line-clamp-2">{c.description}</p>
                <div className="flex gap-2">
                  <button onClick={() => openModal(c)} className="flex-1 flex items-center justify-center gap-1.5 bg-blue-50 text-blue-500 text-xs font-semibold py-2 rounded-lg hover:bg-blue-100">
                    <Edit2 size={12} /> Edit
                  </button>
                  <button onClick={() => handleDelete(c.id)} className="flex-1 flex items-center justify-center gap-1.5 bg-red-50 text-red-500 text-xs font-semibold py-2 rounded-lg hover:bg-red-100">
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="font-black text-gray-900">{modal === 'add' ? 'Add Category' : 'Edit Category'}</h3>
              <button onClick={() => setModal(null)} className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"><X size={16} /></button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Name</label>
                <input type="text" className="input" placeholder="e.g., Home Cleaning" value={form.name} onChange={e => set('name', e.target.value)} required />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-2">Icon</label>
                <div className="flex flex-wrap gap-2">
                  {ICONS.map(icon => (
                    <button key={icon} type="button" onClick={() => set('icon', icon)}
                      className={`w-10 h-10 text-xl rounded-xl border-2 transition-all ${form.icon === icon ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Image URL</label>
                <input type="url" className="input" placeholder="https://images.unsplash.com/..." value={form.image} onChange={e => set('image', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Description</label>
                <textarea className="input resize-none" rows={2} value={form.description} onChange={e => set('description', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Display Order</label>
                <input type="number" className="input" value={form.order} onChange={e => set('order', e.target.value)} min={0} />
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Saving...' : 'Save'}</button>
                <button type="button" onClick={() => setModal(null)} className="flex-1 border border-gray-200 text-gray-600 rounded-xl font-semibold text-sm py-2.5 hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
